/**
 * Builds the navigation state passed to `/admin/setup` when an admin clones
 * an existing campaign. Centralised so every call site (dashboard card menu,
 * campaign detail header) lands the user on the same wizard step with the
 * same pre-filled context - previously each surface re-implemented this and
 * the detail-view path forgot to forward `form_schema`, dumping users on the
 * objectives step instead of the builder.
 *
 * @module lib/clone-session
 */

import type { FormField, InterviewSession } from '../types';
import { cloneFormFields } from './form-capabilities';
import { normalizeConversationDefinition } from './conversation-definition';
import type { ConversationExperienceConfig } from './multimodal-contract';
import { cloneConversationSections } from './conversation-sections';

/** Subset of `InterviewSession` columns that seed the cloned setup wizard. */
export type CloneSourceSession = Pick<
  InterviewSession,
  | 'target_name'
  | 'target_url'
  | 'goal'
  | 'details'
  | 'product_research'
  | 'output_language'
  | 'form_schema'
  | 'conversation_config'
  | 'require_video'
>;

/** Shape consumed by `<CreateInterviewForm>` via `location.state`. */
export interface CloneNavigationState {
  /** Cloned lifecycle references use the newly minted field identities. */
  initialConversationConfig?: ConversationExperienceConfig | null;
  initialContext: {
    targetName: string;
    targetUrl: string | undefined;
    goal: string;
    details: string | undefined;
    productResearch: InterviewSession['product_research'];
    outputLanguage: string | undefined;
  };
  /**
   * Always defined (possibly empty). The wizard treats the mere presence of
   * this prop as the clone signal, skipping the objectives step and landing
   * directly on the form-builder canvas.
   */
  initialFields: FormField[];
}

/**
 * Build the wizard navigation state for cloning a session.
 * Each cloned field gets a fresh id so it cannot collide with the source
 * session's ids in any state map keyed by field id.
 */
export function buildCloneNavigationState(
  session: CloneSourceSession,
): CloneNavigationState {
  const initialContext = {
    targetName: session.target_name,
    targetUrl: session.target_url,
    goal: session.goal,
    details: session.details,
    productResearch: session.product_research,
    outputLanguage: session.output_language,
  };
  const definition = normalizeConversationDefinition({ fields: session.form_schema, config: session.conversation_config, details: session.details, goal: session.goal, requiredVideo: session.require_video });
  const initialFields = cloneFormFields(definition.fields);
  if (definition.conversationConfig.sections) definition.conversationConfig.sections = cloneConversationSections(definition.conversationConfig.sections, definition.fields, initialFields);
  return { initialContext, initialFields, initialConversationConfig: definition.conversationConfig };
}
